/**
 * Organization Store - Zustand store for organization management
 * Handles fetching and caching organization data with proper error handling
 */

import { useEffect } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { Organization } from "@/types/reports"
import { simpleGet } from "@/lib/apiUtils"

interface OrganizationState {
	// Data
	organizations: Organization[]
	selectedOrganization: Organization | null

	// Loading states
	loading: boolean
	loadingById: boolean

	// Error handling
	error: string | null

	// Flags
	initialized: boolean
}

interface OrganizationActions {
	// Data fetching
	fetchAllOrganizations: () => Promise<void>
	fetchOrganizationById: (id: string) => Promise<Organization | null>

	// Selection management
	setSelectedOrganization: (organization: Organization | null) => void

	// Utility methods
	getOrganizationFromCache: (id: string) => Organization | null
	refreshOrganizations: () => Promise<void>

	// State management
	clearError: () => void
	clearStore: () => void

	// Internal methods
	_setLoading: (loading: boolean) => void
	_setLoadingById: (loading: boolean) => void
	_setError: (error: string | null) => void
	_setOrganizations: (organizations: Organization[]) => void
	_addOrganizationToCache: (organization: Organization) => void
}

type OrganizationStore = OrganizationState & OrganizationActions
const debugLog = (...args: unknown[]) => {
	if (import.meta.env.DEV) console.log(...args)
}

const initialState: OrganizationState = {
	organizations: [],
	selectedOrganization: null,
	loading: false,
	loadingById: false,
	error: null,
	initialized: false,
}

export const useOrganizationStore = create<OrganizationStore>()(
	persist(
		(set, get) => ({
			...initialState,

			// === DATA FETCHING ACTIONS ===

			fetchAllOrganizations: async () => {
				const { loading } = get()

				// Prevent concurrent fetches
				if (loading) {
					debugLog("[ORGANIZATION_STORE] Fetch already in progress")
					return
				}

				set({ loading: true, error: null })

				try {
					debugLog("[ORGANIZATION_STORE] Fetching all organizations...")
					const organizations = await simpleGet<Organization[]>("/api/organizations")

					set({
						organizations,
						loading: false,
						initialized: true,
						error: null,
					})

					debugLog(`[ORGANIZATION_STORE] Successfully fetched ${organizations.length} organizations`)
				} catch (error) {
					const errorMessage = error instanceof Error ? error.message : "Failed to fetch organizations"
					console.error("[ORGANIZATION_STORE] Error fetching organizations:", error)

					set({
						loading: false,
						error: errorMessage,
						initialized: true,
					})
				}
			},

			fetchOrganizationById: async (id: string) => {
				if (!id) {
					console.warn("[ORGANIZATION_STORE] fetchOrganizationById called with empty id")
					return null
				}

				// Check cache first
				const cachedOrganization = get().getOrganizationFromCache(id)
				if (cachedOrganization) {
					debugLog(`[ORGANIZATION_STORE] Organization ${id} found in cache`)
					return cachedOrganization
				}

				set({ loadingById: true, error: null })

				try {
					debugLog(`[ORGANIZATION_STORE] Fetching organization by id: ${id}`)
					const organization = await simpleGet<Organization | null>(`/api/organizations/${id}`)

					if (organization) {
						// Add to cache
						get()._addOrganizationToCache(organization)
						debugLog(`[ORGANIZATION_STORE] Successfully fetched organization: ${organization.name}`)
					} else {
						debugLog(`[ORGANIZATION_STORE] Organization not found: ${id}`)
					}

					set({ loadingById: false })
					return organization
				} catch (error) {
					const errorMessage = error instanceof Error ? error.message : "Failed to fetch organization"
					console.error(`[ORGANIZATION_STORE] Error fetching organization ${id}:`, error)

					set({
						loadingById: false,
						error: errorMessage,
					})
					return null
				}
			},

			// === SELECTION MANAGEMENT ===

			setSelectedOrganization: (organization) => {
				debugLog("[ORGANIZATION_STORE] Setting selected organization:", organization?.name || "null")
				set({ selectedOrganization: organization })
			},

			// === UTILITY METHODS ===

			getOrganizationFromCache: (id: string) => {
				const { organizations } = get()
				// Ensure organizations is always an array
				const orgs = Array.isArray(organizations) ? organizations : []
				return orgs.find((org) => org.id === id) || null
			},

			refreshOrganizations: async () => {
				debugLog("[ORGANIZATION_STORE] Refreshing organizations...")
				// Clear current data and refetch
				set({ organizations: [], initialized: false })
				await get().fetchAllOrganizations()
			},

			// === STATE MANAGEMENT ===

			clearError: () => {
				set({ error: null })
			},

			clearStore: () => {
				debugLog("[ORGANIZATION_STORE] Clearing store")
				set(initialState)
			},

			// === INTERNAL METHODS ===

			_setLoading: (loading) => set({ loading }),
			_setLoadingById: (loadingById) => set({ loadingById }),
			_setError: (error) => set({ error }),
			_setOrganizations: (organizations) => set({ organizations }),

			_addOrganizationToCache: (organization) => {
				const { organizations } = get()
				// Ensure organizations is always an array
				const orgs = Array.isArray(organizations) ? organizations : []
				const existingIndex = orgs.findIndex((org) => org.id === organization.id)

				if (existingIndex >= 0) {
					// Update existing organization
					const updatedOrganizations = [...orgs]
					updatedOrganizations[existingIndex] = organization
					set({ organizations: updatedOrganizations })
				} else {
					// Add new organization
					set({ organizations: [...orgs, organization] })
				}
			},
		}),
		{
			name: "organization-store",
			// Only persist essential data, not loading states
			partialize: (state) => ({
				organizations: state.organizations || [],
				selectedOrganization: state.selectedOrganization,
				initialized: state.initialized || false,
			}),
			// Custom storage with error handling and SSR safety
			storage: {
				getItem: (name) => {
					if (typeof window === "undefined") return null
					try {
						const item = localStorage.getItem(name)
						return item ? JSON.parse(item) : null
					} catch (error) {
						console.error(`[ORGANIZATION_STORE] Error reading from localStorage:`, error)
						return null
					}
				},
				setItem: (name, value) => {
					if (typeof window === "undefined") return
					try {
						localStorage.setItem(name, JSON.stringify(value))
					} catch (error) {
						console.error(`[ORGANIZATION_STORE] Error writing to localStorage:`, error)
					}
				},
				removeItem: (name) => {
					if (typeof window === "undefined") return
					try {
						localStorage.removeItem(name)
					} catch (error) {
						console.error(`[ORGANIZATION_STORE] Error removing from localStorage:`, error)
					}
				},
			},
		},
	),
)

// === SELECTORS ===

/**
 * Hook to get organizations with automatic fetching
 */
export const useOrganizations = () => {
	const store = useOrganizationStore()

	// Auto-fetch if not initialized (must be in useEffect, not during render)
	useEffect(() => {
		if (!store.initialized && !store.loading) {
			store.fetchAllOrganizations()
		}
	}, [store.initialized, store.loading, store.fetchAllOrganizations])

	return {
		organizations: Array.isArray(store.organizations) ? store.organizations : [],
		loading: store.loading || false,
		error: store.error,
		refresh: store.refreshOrganizations,
	}
}
/**
 * Hook to get a specific organization by ID with automatic fetching
 */
export const useOrganizationById = (id: string | null) => {
	const store = useOrganizationStore()

	// Get from cache if available
	const organization = id ? store.getOrganizationFromCache(id) : null

	// Auto-fetch if not in cache and not currently loading
	useEffect(() => {
		if (id && !organization && !store.loadingById) {
			store.fetchOrganizationById(id)
		}
	}, [id, organization, store.loadingById, store.fetchOrganizationById])

	return {
		organization,
		loading: store.loadingById,
		error: store.error,
	}
}

/**
 * Hook for organization selection management
 */
export const useOrganizationSelection = () => {
	const store = useOrganizationStore()

	return {
		selectedOrganization: store.selectedOrganization,
		setSelectedOrganization: store.setSelectedOrganization,
		clearSelection: () => store.setSelectedOrganization(null),
	}
}

// === UTILITIES ===

/**
 * Get organization by ID synchronously (cache only)
 */
export const getOrganizationFromCache = (id: string): Organization | null => {
	return useOrganizationStore.getState().getOrganizationFromCache(id)
}

/**
 * Check if organizations are loaded
 */
export const areOrganizationsLoaded = (): boolean => {
	return useOrganizationStore.getState().initialized || false
}

/**
 * Get all organizations synchronously (cache only)
 */
export const getAllOrganizationsFromCache = (): Organization[] => {
	const organizations = useOrganizationStore.getState().organizations
	return Array.isArray(organizations) ? organizations : []
}
